import { runAllValidations } from './offerValidationService.js';
import { usePersistedDataStore } from '../../../store.js';
import { completeCaseStatus } from '../../../../common/CaseStatusUpdater.js';
import { toMySQLDateTime } from '../../../../common/helper_functions.js';
import { isCaseSettled, isAssignmentComplete } from '../utils/helpers.js';
import { getMostRecentAventineComments, handleOffersAndWorkupsEmailWorkflow } from '../../Offers/offerService.js';
import { uploadOffersToS3 } from '../../Offers/s3UploadService.js';
import { getTownViewXLSKeys } from '../config/columns.js';
import { updateOffersBatch } from './realtimeOfferService.js';
import { createOfferContext } from '../../../../utils/offerContextUtils.js';
import { applyOfferRoundingGuard } from '../../Settlement/SettlementRounding.js';
import { validateOfferEmailWorkflow } from '../../EmailFactory/Validations/offerEmailValidations.js';
import { useOfferOptionsStore } from '../../Offers/offerOptionsStore.js';
import logger from '../../../../utils/logger';

/**
 * Unified entry point for all offer finalization and email sending operations
 * This service consolidates all validation and workflow logic in one place
 */

/**
 * Main unified function for handling offers - finalizes if needed, sends emails
 * @param {Object} options - Options for the operation
 * @param {Array} options.selectedRows - Selected rows (if any)
 * @param {Array} options.allRows - All available rows
 * @param {Function} options.showResendDialog - Function to show resend dialog
 * @param {Function} options.showEmailPreviewDialog - Function to show email preview dialog
 * @param {Object} options.s3UploadResult - S3 upload result (optional)
 * @param {Object} options.user - Current user object from UserContext
 * @param {string} options.action - Action to perform: 'finalize', 'send', or 'finalizeAndSend'
 * @param {Object} options.validationOptions - Additional validation options
 * @param {boolean} options.skipSubsetCheck - Skip subset warning check (default: false)
 * @param {boolean} options.resumeAfterFinalize - Resume with send after finalizing (default: false)
 * @returns {Promise<Object>} Result of the operation
 */
export const handleUnifiedOfferAction = async (options) => {
  logger.log('🔄 [UNIFIED OFFER SERVICE] handleUnifiedOfferAction called:', {
    action: options.action,
    hasS3UploadResult: !!options.s3UploadResult,
    s3UploadResult: options.s3UploadResult,
    selectedRowsCount: options.selectedRows?.length,
    allRowsCount: options.allRows?.length,
    skipSubsetCheck: options.skipSubsetCheck,
    timestamp: new Date().toISOString()
  });
  
  const {
    selectedRows,
    allRows,
    s3UploadResult,
    user,
    action = 'finalizeAndSend',
    validationOptions = {},
    skipSubsetCheck = false,
    skipWorkupsAboveIfmvCheck = false,
    skipOfferUpdate = false,
  } = options;

  // Get all persisted state and functions from the store
  const store = usePersistedDataStore.getState();
  const {
    negotiationObj,
    setEmailingOffers
  } = store;
  
  // Filter allRows to only include unsettled cases for consistent validation
  const unsettledAllRows = allRows.filter(row => !isCaseSettled(row));
  
  try {
    // Validate required parameters
    if (!negotiationObj || !negotiationObj.cases) {
      throw new Error('No negotiation data available');
    }

    if (!selectedRows || selectedRows.length === 0) {
      throw new Error('No cases selected to process');
    }

    if (!user) {
      throw new Error('User information is required');
    }

    // Check user permissions
    if (user?.userAccess?.level === 'Training') {
      throw new Error('Training users cannot send email offers. This action is restricted.');
    }

    logger.log(`🔍 [UNIFIED OFFER] Starting ${action} action for ${selectedRows.length} cases`);

    // On initial invocation (no S3 upload result), run validations and trigger S3 upload
    if (!s3UploadResult && (action === 'send' || action === 'finalizeAndSend')) {
      logger.log('🔍 [UNIFIED OFFER] Initial invocation - running validations and triggering S3 upload');
      // Run all validations first, then trigger S3 upload
      // Email preview will only be generated in S3 upload callback
    }

    // Step 1: Check if user is attempting to send all cases but not all are finalized
    logger.log(`🔍 [SEND ALL VALIDATION] Checking: action=${action}, selectedRows=${selectedRows.length}, unsettledAllRows=${unsettledAllRows.length}, villageRun=${negotiationObj?.villageRun}`);
    if (
      (action === 'send' || action === 'finalizeAndSend') &&
      selectedRows.length === unsettledAllRows.length
    ) {
      logger.log('🔍 [SEND ALL VALIDATION] Triggering send all validation...');
      const finalizedCases = selectedRows.filter(row => {
        const caseFromState = negotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
        const isFinalized = isAssignmentComplete(caseFromState, 'CaseReview');
        
        return isFinalized;
      });
      
      logger.log(`🔍 [SEND ALL VALIDATION] Results: ${finalizedCases.length}/${selectedRows.length} cases finalized`);
      
      // Debug: Show unfinalized cases
      const unfinalizedCases = selectedRows.filter(row => {
        const caseFromState = negotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
        const isFinalized = isAssignmentComplete(caseFromState, 'CaseReview');
        return !isFinalized;
      });
      
      if (unfinalizedCases.length > 0) {
        logger.log('🔍 [SEND ALL VALIDATION] Unfinalized cases:', unfinalizedCases.slice(0, 5).map(row => ({
          parcel_id: row.parcel_id,
          CaseReview: isAssignmentComplete(negotiationObj.cases.find(c => c.parcel_id === row.parcel_id), 'CaseReview')
        })));
      }
      
      if (finalizedCases.length !== selectedRows.length) {
        const unfinalizedCount = selectedRows.length - finalizedCases.length;
        const errorMessage = `Not all cases are finalized. ${unfinalizedCount} case(s) still need to be finalized before sending.`;
        
        logger.log('❌ [UNIFIED OFFER] Send all validation failed:', {
          totalCases: selectedRows.length,
          finalizedCases: finalizedCases.length,
          unfinalizedCases: unfinalizedCount,
          sampleUnfinalized: selectedRows.filter(row => {
            const hasOfferValue = row.OfferValue !== null && 
                                 row.OfferValue !== 'None' && 
                                 row.OfferValue !== '' && 
                                 row.OfferValue !== 0 &&
                                 !isNaN(Number(row.OfferValue)) &&
                                 Number(row.OfferValue) > 0;
            return !hasOfferValue;
          }).slice(0, 3).map(row => ({ parcel_id: row.parcel_id, OfferValue: row.OfferValue }))
        });
        
        return {
          success: false,
          error: errorMessage,
          action: 'validation_failed',
          requiresFinalization: true,
          unfinalizedCount
        };
      }
    }

    // Step 2: Run validations
    if (action === 'send' || action === 'finalizeAndSend') {
      // For send flows, use the shared EmailFactory validation stack
      const emailValidation = validateOfferEmailWorkflow({
        selectedRows,
        allRows,
        negotiationObj,
        user,
        skipSubsetCheck,
        skipWorkupsAboveIfmvCheck,
      });

      logger.log('🔍 [UNIFIED OFFER] Email validation result:', {
        isValid: emailValidation.isValid,
        validationType: emailValidation.validationType,
      });

      if (!emailValidation.isValid) {
        logger.error('❌ [UNIFIED OFFER] Email validation failed:', emailValidation.error, 'type:', emailValidation.validationType);

        if (emailValidation.validationType === 'missing_offers') {
          return {
            success: false,
            requiresMissingOffersDialog: true,
            missingOffers: emailValidation.missingOffers || [],
            error: emailValidation.error,
            action: 'validation_failed',
          };
        }

        if (emailValidation.validationType === 'missing_workup_values') {
          return {
            success: false,
            error: emailValidation.error,
            action: 'validation_failed',
            validationType: emailValidation.validationType,
            missingWorkupValues: emailValidation.missingWorkupValues || [],
          };
        }

        if (emailValidation.validationType === 'missing_comps') {
          return {
            success: false,
            error: emailValidation.error,
            action: 'validation_failed',
            validationType: emailValidation.validationType,
            missingCompsCases: emailValidation.missingCompsCases || [],
          };
        }

        if (emailValidation.validationType === 'subset_warning') {
          return {
            success: false,
            requiresSubsetWarning: true,
            openCases: emailValidation.openCasesNotSelected || [],
            action: 'subset_warning',
          };
        }

        if (emailValidation.validationType === 'workups_above_ifmv') {
          return {
            success: false,
            requiresWorkupsAboveIfmvDialog: true,
            workupsAboveIfmv: emailValidation.workupsAboveIfmv || [],
            validationType: 'workups_above_ifmv',
            error: emailValidation.error,
            action: 'validation_failed',
          };
        }

        // Fallback for other validation failures (e.g., not finalized)
        throw new Error(emailValidation.error || 'Offer email validation failed');
      }

      logger.log('✅ [UNIFIED OFFER] Email validations passed, proceeding with action');
    } else {
      // For non-send actions, keep using the existing unified validations
      const validation = runAllValidations(selectedRows, {
        includeSettled: false,
        requireOffers: true,
        ...validationOptions,
      });

      logger.log('🔍 [UNIFIED OFFER] Validation result:', {
        isValid: validation.isValid,
        missingOffersCount: validation.offerValidation.missingOffers.length,
        unsettledCasesCount: validation.offerValidation.unsettledCases.length,
        settledCasesCount: validation.offerValidation.settledCases.length,
      });

      if (!validation.isValid) {
        logger.error('❌ [UNIFIED OFFER] Validation failed:', validation.error);

        if (validation.offerValidation.missingOffers.length > 0) {
          return {
            success: false,
            requiresMissingOffersDialog: true,
            missingOffers: validation.offerValidation.missingOffers,
            error: validation.error,
            action: 'validation_failed',
          };
        }

        throw new Error(validation.error);
      }

      logger.log('✅ [UNIFIED OFFER] All validations passed, proceeding with action');
    }

    // Step 5: Handle different actions
    switch (action) {
      case 'finalize':
        return await handleFinalizeAction({
          selectedRows,
          allRows,
          user,
          skipOfferUpdate,
        });

      case 'send': {
        // If no S3 upload result, run validations and trigger S3 upload
        logger.log('🔍 [UNIFIED OFFER] SEND action - delegating to EmailFactory-based combined workflow (offers only)');

        const store = usePersistedDataStore.getState();
        const { negotiationObj } = store;

        // Ensure offers XLS exists
        const offersOnlyWorkupOpts = { includeWorkups: false };
        const uploadResult = s3UploadResult || (await uploadOffersToS3({
          caseData: allRows,
          selectedRows,
          keys: getTownViewXLSKeys({
            includeCompsInOffersSheet: useOfferOptionsStore.getState().includeCompsInOffersSpreadsheet,
            workupOptions: offersOnlyWorkupOpts,
          }),
          townView: true,
          villageMode: false
        }));

        if (!uploadResult.success) {
          logger.error('❌ [UNIFIED OFFER] S3 upload failed:', uploadResult.error);
          setEmailingOffers(false);
          return {
            success: false,
            error: `S3 upload failed: ${uploadResult.error}`,
            action: 'send'
          };
        }

        logger.log('✅ [UNIFIED OFFER] S3 upload successful, preparing offers-only email via EmailFactory');

        const combinedResult = await handleOffersAndWorkupsEmailWorkflow({
          selectedRows,
          allRows,
          negotiationObj,
          user,
          workupType: 'fnmaSummary',
          skipSubsetCheck,
          workupOptions: offersOnlyWorkupOpts,
          skipWorkupsAboveIfmvCheck,
          RegenerateWorkups: false
        });

        setEmailingOffers(false);
        return {
          ...combinedResult,
          s3UploadResult: uploadResult
        };
      }

      case 'finalizeAndSend': {
        // First finalize, then send
        const finalizeResult = await handleFinalizeAction({
          selectedRows,
          allRows,
          user
        });

        if (!finalizeResult.success) {
          return finalizeResult;
        }

        // After finalization, ensure offers XLS exists, then delegate to EmailFactory-based offers-only flow
        logger.log('🔍 [UNIFIED OFFER] FinalizeAndSend - finalization complete, delegating to offers-only EmailFactory flow');

        const store = usePersistedDataStore.getState();
        const { negotiationObj } = store;

        const offersOnlyWorkupOptsFs = { includeWorkups: false };
        const uploadResult = s3UploadResult || (await uploadOffersToS3({
          caseData: allRows,
          selectedRows,
          keys: getTownViewXLSKeys({
            includeCompsInOffersSheet: useOfferOptionsStore.getState().includeCompsInOffersSpreadsheet,
            workupOptions: offersOnlyWorkupOptsFs,
          }),
          townView: true,
          villageMode: false
        }));

        if (!uploadResult.success) {
          logger.error('❌ [UNIFIED OFFER] S3 upload failed (finalizeAndSend):', uploadResult.error);
          setEmailingOffers(false);
          return {
            success: false,
            error: `S3 upload failed: ${uploadResult.error}`,
            action: 'send'
          };
        }

        const combinedResult = await handleOffersAndWorkupsEmailWorkflow({
          selectedRows,
          allRows,
          negotiationObj,
          user,
          workupType: 'fnmaSummary',
          skipSubsetCheck,
          workupOptions: offersOnlyWorkupOptsFs,
          skipWorkupsAboveIfmvCheck,
          RegenerateWorkups: false
        });

        setEmailingOffers(false);
        return {
          ...combinedResult,
          s3UploadResult: uploadResult
        };
      }

      default:
        throw new Error(`Unknown action: ${action}`);
    }

  } catch (error) {
    logger.error('Error in unified offer action:', error);
    // Clear emailing state on error
    if (action === 'send' || action === 'finalizeAndSend') {
      setEmailingOffers(false);
    }
    return { 
      success: false, 
      error: error.message,
      action: 'error'
    };
  }
};

/**
 * Handle finalization action - unified finalization with backend API calls and frontend state updates
 * @param {Object} options - Options for finalization
 * @returns {Promise<Object>} Result of finalization
 */
const handleFinalizeAction = async (options) => {
  const { selectedRows, allRows, user, skipOfferUpdate = false } = options;

  // Get all persisted state and functions from the store
  const store = usePersistedDataStore.getState();
  const {
    negotiationObj,
    setNegotiationObj,
    setNegotiationObjVersion
  } = store;
  
  const currentUserId = user?.userId;

  try {
    // Settled cases are already resolved and are ignored by mark-as-reviewed.
    const rowsToReview = selectedRows.filter((row) => row?.parcel_id && !row._isSettled && !isCaseSettled(row));

    logger.log('🔍 [UNIFIED OFFER] Marking cases as reviewed for', rowsToReview.length, 'cases', {
      selected: selectedRows.length,
      skippedSettled: selectedRows.length - rowsToReview.length,
    });
    
    // Extract parcel IDs from unsettled rows for case status updates
    const finalizedParcelIds = rowsToReview.map(row => row.parcel_id);

    // Check if we have any cases to mark as reviewed
    if (finalizedParcelIds.length === 0) {
      throw new Error('No unsettled cases found to mark as reviewed.');
    }
    
    // STEP 0: Update offers FIRST (before case status updates) - skip if already done
    if (!skipOfferUpdate) {
      try {
        logger.log('🔄 Updating offers before finalizing case status...');
        
        // Prepare offers for batch update
        const offersToUpdate = rowsToReview
          .filter(row => {
            // Only include rows with valid offer values
            const hasValidOffer = row.OfferValue !== null && 
                                 row.OfferValue !== 'None' && 
                                 row.OfferValue !== '' && 
                                 row.OfferValue !== 0 &&
                                 !isNaN(Number(row.OfferValue)) &&
                                 Number(row.OfferValue) > 0;
            return hasValidOffer && row.parcel_id;
          })
          .map(row => {
            // Apply unified offer rounding guard
            const currentAV = row.PropertyAssessment || row.AssessmentVillage || 0;
            const offerValueAV = Number(row.OfferValue);
            const finalOfferValue = applyOfferRoundingGuard(offerValueAV, currentAV);
            
            const caseItem = negotiationObj.cases?.find((c) => c.parcel_id === row.parcel_id) || row;

            return {
              parcelId: row.parcel_id,
              offerValue: finalOfferValue,
              portfolioOffer: row.PortfolioOffer ? Number(row.PortfolioOffer) : null,
              offeredBy: 'Aventine',
              offerDate: new Date().toISOString(),
              comments: getMostRecentAventineComments(caseItem),
            };
          });

        if (offersToUpdate.length > 0) {
          // Create context for batch update
          const context = createOfferContext(user, negotiationObj, 'Manual');
          
          // Update offers in database
          const offerUpdateResult = await updateOffersBatch(offersToUpdate, context, {
            liveOffer: 0,
            updateFrontend: true
          });

          if (offerUpdateResult.success) {
            logger.log(`✅ Successfully updated ${offerUpdateResult.processed_offers} offers before finalization`);
          } else {
            logger.warn('⚠️ Failed to update offers before finalization:', offerUpdateResult.error);
            // Continue with finalization even if offer update fails
          }
        }
        
      } catch (error) {
        logger.warn('⚠️ Error updating offers before finalization:', error.message);
        // Continue with finalization even if offer update fails
      }
    } else {
      logger.log('⏭️ [UNIFIED OFFER] Skipping offer update (already completed)');
    }
    
    // STEP 1: Complete CaseReview tasks for finalized parcels
    let caseReviewTasksExist = false;
    try {
      logger.log('🔄 Completing CaseReview tasks...');
      
      await completeCaseStatus(finalizedParcelIds, 'CaseReview', {
        muniCode: negotiationObj.MuniCode,
        taxYear: negotiationObj.TaxYear,
        userId: currentUserId,
        userAccessLevel: null, // This would need to be passed from the calling component
        villageRun: negotiationObj?.villageRun || 0,
      });
      
      caseReviewTasksExist = true;
      logger.log('✅ Successfully completed CaseReview tasks');
      
    } catch (error) {
      logger.log('❌ Error completing CaseReview tasks:', error.message);
      caseReviewTasksExist = false;
    }

    // STEP 2: Update CaseAssignmentInfo DateCompleted for CaseReview
    try {
      logger.log(`🔄 Updating case status for ${finalizedParcelIds.length} cases`);
      
      const updatedNegotiationObj = { ...negotiationObj };
      
      finalizedParcelIds.forEach(parcelId => {
        const caseIndex = updatedNegotiationObj.cases.findIndex(
          (caseItem) => caseItem.parcel_id === parcelId
        );
        
        if (caseIndex !== -1) {
          const originalCase = updatedNegotiationObj.cases[caseIndex];
          const updatedAssignmentInfo = Array.isArray(originalCase.CaseAssignmentInfo)
            ? [...originalCase.CaseAssignmentInfo]
            : [];
          const caseReviewIndex = updatedAssignmentInfo.findIndex(info => info.ReviewType === 'CaseReview');
          if (caseReviewIndex !== -1) {
            updatedAssignmentInfo[caseReviewIndex] = {
              ...updatedAssignmentInfo[caseReviewIndex],
              Assignee: currentUserId || null,
              DateCompleted: toMySQLDateTime(new Date())
            };
          } else {
            updatedAssignmentInfo.push({
              ReviewType: 'CaseReview',
              Assignee: currentUserId || null,
              DateAssigned: toMySQLDateTime(new Date()),
              DateCompleted: toMySQLDateTime(new Date()),
              MiscDesc: null
            });
          }

          updatedNegotiationObj.cases[caseIndex] = {
            ...originalCase,
            SelectedIndexRegular: originalCase.SelectedIndexRegular || [],
            SelectedIndexOptimized: originalCase.SelectedIndexOptimized || [],
            CaseAssignmentInfo: updatedAssignmentInfo,
          };
        }
      });
      
      // Update the frontend with case status changes
      setNegotiationObj(updatedNegotiationObj);
      setNegotiationObjVersion(prev => prev + 1);
      
      logger.log(`✅ [UNIFIED OFFER] Successfully updated case status for ${finalizedParcelIds.length} cases`);
      
    } catch (error) {
      logger.warn('⚠️ Error updating case status:', error.message);
      // Don't fail the entire operation if case status update fails
    }

    logger.log('✅ [UNIFIED OFFER] Successfully marked cases as reviewed');
    logger.log(`   - ${finalizedParcelIds.length} cases marked as reviewed`);
    logger.log(`   - ${caseReviewTasksExist ? 'Updated existing CaseReview tasks' : 'Directly created Negotiation tasks'}`);
    logger.log(`   - Case assignment status updated`);
    
    return {
      success: true,
      message: `Successfully marked ${finalizedParcelIds.length} case(s) as reviewed`,
      action: 'finalize',
      finalizedCount: finalizedParcelIds.length
    };

  } catch (error) {
    logger.error('Error finalizing offers:', error);
    return {
      success: false,
      error: error.message,
      action: 'finalize'
    };
  }
};




/**
 * Simplified function for just validation (useful for UI state management)
 * @param {Array} selectedRows - Selected rows to validate
 * @param {Object} validationOptions - Validation options
 * @returns {Object} Validation result
 */
export const validateOffers = (selectedRows, validationOptions = {}) => {
  if (!selectedRows || selectedRows.length === 0) {
    return {
      isValid: false,
      error: 'No cases selected for validation',
      missingOffers: [],
      unsettledCases: [],
      settledCases: []
    };
  }

  return runAllValidations(selectedRows, {
    includeSettled: false,
    requireOffers: true,
    ...validationOptions
  });
};

/**
 * Check if cases are ready for finalization
 * @param {Array} selectedRows - Selected rows to check
 * @returns {Object} Readiness check result
 */
export const checkFinalizationReadiness = (selectedRows) => {
  const validation = validateOffers(selectedRows);
  
  return {
    isReady: validation.isValid,
    missingOffers: validation.offerValidation.missingOffers,
    unsettledCases: validation.offerValidation.unsettledCases,
    settledCases: validation.offerValidation.settledCases,
    error: validation.error
  };
};

/**
 * Check if cases are ready for sending
 * @param {Array} selectedRows - Selected rows to check
 * @returns {Object} Readiness check result
 */
export const checkSendingReadiness = (selectedRows) => {
  const validation = validateOffers(selectedRows);
  
  return {
    isReady: validation.isValid,
    missingOffers: validation.offerValidation.missingOffers,
    unfinalizedCases: validation.finalizedValidation.unfinalizedCases,
    error: validation.error
  };
};

/**
 * Get comprehensive status for UI display
 * @param {Array} selectedRows - Selected rows to analyze
 * @returns {Object} Comprehensive status information
 */
export const getOfferStatus = (selectedRows) => {
  if (!selectedRows || selectedRows.length === 0) {
    return {
      hasSelection: false,
      canFinalize: false,
      canSend: false,
      needsOffers: false,
      isReady: false,
      status: 'no_selection'
    };
  }

  const validation = validateOffers(selectedRows);
  const finalizationCheck = checkFinalizationReadiness(selectedRows);
  const sendingCheck = checkSendingReadiness(selectedRows);

  return {
    hasSelection: true,
    canFinalize: finalizationCheck.isReady,
    canSend: sendingCheck.isReady,
    needsOffers: validation.offerValidation.missingOffers.length > 0,
    isReady: validation.isValid,
    status: validation.isValid ? 'ready' : 'needs_attention',
    validation,
    finalizationCheck,
    sendingCheck
  };
};
